/**
 * Shoppable Video Section Script
 * Handles video playback, slider initialization, and product interactions
 *
 * Fix (09.2026): definitions wrapped in customElements guards so the script
 * doesn't crash with "Identifier 'DeferredMedia' has already been declared"
 * (main.js already defines deferred-media / load-media).
 */

if (!customElements.get('shoppable-video-section')) {
class ShoppableVideoSection extends HTMLElement {
  constructor() {
    super();
    this.layout = this.dataset.layout;
    this.autoplay = this.dataset.autoplay === 'true';
    this.swiper = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (this.layout === 'slider') {
      this.initSlider();
    }
    
    this.initVideoComponents();
    this.initProductHotspots();
    this.initPlayButtons();
  }

  /**
   * Initialize Swiper slider
   */
  initSlider() {
    // Check if Swiper is loaded
    if (typeof Swiper === 'undefined') {
      this.loadSwiper().then(() => {
        this.createSwiper();
      });
    } else {
      this.createSwiper();
    }
  }

  /**
   * Load Swiper library dynamically
   */
  loadSwiper() {
    return new Promise((resolve, reject) => {
      // Load Swiper CSS
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
      document.head.appendChild(cssLink);

      // Load Swiper JS
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Create Swiper instance
   */
  createSwiper() {
    const swiperElement = this.querySelector('.swiper');
    if (!swiperElement) return;

    const showArrows = this.querySelector('.swiper-button-prev') !== null;
    const showPagination = this.querySelector('.swiper-pagination') !== null;

    this.swiper = new Swiper(swiperElement, {
      slidesPerView: 1,
      spaceBetween: 16,
      loop: false,
      navigation: showArrows ? {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      } : false,
      pagination: showPagination ? {
        el: '.swiper-pagination',
        clickable: true,
      } : false,
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 24,
        },
        1280: {
          slidesPerView: 4,
          spaceBetween: 24,
        }
      },
      on: {
        slideChange: () => {
          // Pause all videos when slide changes
          this.pauseAllVideos();
        }
      }
    });
  }

  /**
   * Initialize video components
   */
  initVideoComponents() {
    const videoComponents = this.querySelectorAll('video-component');
    
    videoComponents.forEach(component => {
      const video = component.querySelector('video');
      const iframe = component.querySelector('iframe');
      
      if (video) {
        this.setupNativeVideo(video, component);
      } else if (component.dataset.videoUrl) {
        this.setupExternalVideo(component);
      }
    });
  }

  /**
   * Setup native HTML5 video
   */
  setupNativeVideo(video, component) {
    video.addEventListener('play', () => {
      this.onVideoPlay(component);
    });

    video.addEventListener('pause', () => {
      this.onVideoPause(component);
    });

    // Autoplay on hover for grid layout
    if (this.layout === 'grid' && !this.autoplay) {
      const videoItem = component.closest('.shoppable-video-item');
      
      videoItem.addEventListener('mouseenter', () => {
        video.play().catch(err => console.log('Autoplay prevented:', err));
      });

      videoItem.addEventListener('mouseleave', () => {
        video.pause();
        video.currentTime = 0;
      });
    }
  }

  /**
   * Setup external video (YouTube/Vimeo)
   */
  setupExternalVideo(component) {
    const videoUrl = component.dataset.videoUrl;
    
    if (!videoUrl) return;

    const iframe = this.createIframe(videoUrl);
    component.appendChild(iframe);
  }

  /**
   * Create iframe for external video
   */
  createIframe(url) {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;

    // Parse URL and create embed URL
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = this.extractYouTubeId(url);
      iframe.src = `https://www.youtube.com/embed/${videoId}?rel=0`;
    } else if (url.includes('vimeo.com')) {
      const videoId = this.extractVimeoId(url);
      iframe.src = `https://player.vimeo.com/video/${videoId}`;
    }

    return iframe;
  }

  /**
   * Extract YouTube video ID
   */
  extractYouTubeId(url) {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  }

  /**
   * Extract Vimeo video ID
   */
  extractVimeoId(url) {
    const regExp = /vimeo.*\/(\d+)/i;
    const match = url.match(regExp);
    return match ? match[1] : null;
  }

  /**
   * Initialize product hotspots
   */
  initProductHotspots() {
    const hotspots = this.querySelectorAll('.product-hotspot');
    
    hotspots.forEach(hotspot => {
      hotspot.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleProductCard(hotspot);
      });
    });

    // Close product cards when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.product-card-popup') && !e.target.closest('.product-hotspot')) {
        this.closeAllProductCards();
      }
    });

    // Close buttons
    const closeButtons = this.querySelectorAll('.close-popup');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.closeAllProductCards();
      });
    });
  }

  /**
   * Toggle product card visibility
   */
  toggleProductCard(hotspot) {
    const productId = hotspot.dataset.productId;
    const productCard = this.querySelector(`[data-product-card="${productId}"]`);
    
    if (!productCard) return;

    // Close all other cards
    this.closeAllProductCards();

    // Toggle current card
    productCard.classList.remove('hidden');
    productCard.classList.add('show');
  }

  /**
   * Close all product cards
   */
  closeAllProductCards() {
    const cards = this.querySelectorAll('.product-card-popup');
    cards.forEach(card => {
      card.classList.remove('show');
      card.classList.add('hidden');
    });
  }

  /**
   * Initialize play buttons
   */
  initPlayButtons() {
    const playButtons = this.querySelectorAll('.js-load-media');
    
    playButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const deferredMedia = button.closest('deferred-media');
        this.loadDeferredMedia(deferredMedia);
      });
    });
  }

  /**
   * Load deferred media (video)
   */
  loadDeferredMedia(deferredMedia) {
    if (!deferredMedia) return;

    const template = deferredMedia.querySelector('template');
    if (!template) return;

    const content = template.content.cloneNode(true);
    deferredMedia.appendChild(content);

    const videoComponent = deferredMedia.querySelector('video-component');
    const video = videoComponent?.querySelector('video');

    if (video) {
      video.play().catch(err => console.log('Play prevented:', err));
    }

    // Hide play button
    const playButton = deferredMedia.querySelector('.js-load-media');
    if (playButton) {
      playButton.style.display = 'none';
    }

    // Mark as playing
    const videoItem = deferredMedia.closest('.shoppable-video-item');
    if (videoItem) {
      videoItem.classList.add('is-playing');
    }
  }

  /**
   * Handle video play event
   */
  onVideoPlay(component) {
    const videoItem = component.closest('.shoppable-video-item');
    if (videoItem) {
      videoItem.classList.add('is-playing');
    }
  }

  /**
   * Handle video pause event
   */
  onVideoPause(component) {
    const videoItem = component.closest('.shoppable-video-item');
    if (videoItem) {
      videoItem.classList.remove('is-playing');
    }
  }

  /**
   * Pause all videos in the section
   */
  pauseAllVideos() {
    const videos = this.querySelectorAll('video');
    videos.forEach(video => {
      video.pause();
    });
  }

  /**
   * Cleanup
   */
  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }
}

// Define custom element
customElements.define('shoppable-video-section', ShoppableVideoSection);
}

if (!customElements.get('load-media')) {
/**
 * Load Media Component
 * Handles loading video on click
 */
class LoadMedia extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    const deferredMediaBtn = this.closest('.video-section')?.querySelector('.js-load-media');
    
    if (button && deferredMediaBtn) {
      button.addEventListener('click', () => deferredMediaBtn.click());
    }
  }
}

customElements.define('load-media', LoadMedia);
}

if (!customElements.get('deferred-media')) {
/**
 * Deferred Media Component
 * Handles lazy loading of media
 */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.loaded = false;
  }

  connectedCallback() {
    const button = this.querySelector('.js-load-media');
    
    if (button) {
      button.addEventListener('click', this.loadContent.bind(this));
    }
  }

  loadContent(event) {
    if (this.loaded) return;
    
    event?.preventDefault();
    
    const content = this.querySelector('template')?.content;
    if (content) {
      this.appendChild(content.cloneNode(true));
      this.loaded = true;
      
      // Auto-play if video
      const video = this.querySelector('video');
      if (video) {
        video.play().catch(err => console.log('Autoplay prevented:', err));
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);
}
